import React from 'react';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import commonArray from './common/commonArray';
import { Badge } from './ui/badge';

interface GraphProps {
    heading: string;
    Match: string | null;
    run: string;
    data: any[];
    xAxis?: string;
    yAxis?: string;
    dashboadGraph: (run: string, timeframe: string) => void;
    loading?: boolean;
    badgeText?: string;
    subtitle?: string;
    legendText?: string;
    extraSummary?: string;
}

const Graph: React.FC<GraphProps> = (props) => {
    const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    const monthOrder = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const getXAxisDataKey = () => {
        if (props.Match === 'DAILY') return 'hour';
        if (props.Match === 'WEEKLY') return 'day';
        if (props.Match === 'MONTHLY') return 'date';
        if (props.Match === 'YEARLY') return 'month';
        return props.xAxis || 'hour';
    };

    const getYAxisDataKey = () => {
        if (props.run === 'product') return 'total_products';
        if (props.run === 'sales') return 'total_orders';
        return props.yAxis || 'total_products';
    };

    const xAxisDataKey = getXAxisDataKey();
    const yAxisDataKey = getYAxisDataKey();

    // 100% dynamic API data
    const chartData = [...(props.data || [])].sort((a, b) => {
        if (props.Match === 'WEEKLY') {
            return dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day);
        }
        if (props.Match === 'YEARLY') {
            return monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month);
        }
        if (props.Match === 'DAILY') {
            return parseInt(a.hour) - parseInt(b.hour);
        }
        if (props.Match === 'MONTHLY') {
            return parseInt(a.date) - parseInt(b.date);
        }
        return 0;
    });

    const isProduct = props.run === 'product';
    const gradientId = `gradient-${props.run}`;
    const brandColor = '#1AA7DE'; // Old project theme color

    // Calculate dynamic total from chart data
    const totalSum = chartData.reduce((acc, curr) => acc + (Number(curr[yAxisDataKey]) || 0), 0);

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-slate-900 text-white px-3.5 py-2 rounded-xl text-sm shadow-xl border border-slate-800">
                    <p className="font-semibold m-0">{label}: {payload[0].value} {isProduct ? 'Products' : 'Orders'}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="w-full xl:w-1/2 p-2">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 sm:p-7 hover:shadow-md transition-all duration-200">
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h4 className="text-lg sm:text-xl font-bold text-slate-800 m-0">{props.heading}</h4>
                        </div>
                        {props.subtitle && (
                            <p className="text-sm text-slate-500 mt-1 mb-0 leading-relaxed">
                                {props.subtitle}
                            </p>
                        )}
                    </div>

                    {/* Timeframe pill switcher */}
                    <div className="inline-flex bg-slate-100/90 p-1.5 rounded-xl gap-1">
                        {commonArray.buttonArray.map((timeframe, index: number) => {
                            const isActive = (props.Match || 'DAILY').toUpperCase() === timeframe.toUpperCase();
                            return (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => props.dashboadGraph(props.run, timeframe.toUpperCase())}
                                    disabled={props.loading}
                                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${isActive
                                            ? 'bg-white text-slate-900 shadow-sm font-bold'
                                            : 'text-slate-500 hover:text-slate-800 font-semibold'
                                        }`}
                                >
                                    {timeframe}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Area Chart */}
                <div className="w-full h-[260px]">
                    {chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={brandColor} stopOpacity={0.25} />
                                        <stop offset="95%" stopColor={brandColor} stopOpacity={0.0} />
                                    </linearGradient>
                                </defs>
                                <XAxis
                                    dataKey={xAxisDataKey}
                                    stroke="#94a3b8"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={{ stroke: '#f1f5f9' }}
                                />
                                <YAxis
                                    stroke="#94a3b8"
                                    fontSize={12}
                                    tickLine={false}
                                    axisLine={{ stroke: '#f1f5f9' }}
                                    allowDecimals={false}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Area
                                    type="monotone"
                                    dataKey={yAxisDataKey}
                                    stroke={brandColor}
                                    strokeWidth={2.5}
                                    fillOpacity={1}
                                    fill={`url(#${gradientId})`}
                                    activeDot={{ r: 6, fill: brandColor, stroke: '#fff', strokeWidth: 2 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    ) : (
                        <div className="h-full flex items-center justify-center text-slate-400 text-sm font-medium">
                            No data available for this timeframe
                        </div>
                    )}
                </div>

                {/* Footer Legend */}
                <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-100 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                        <span className="w-3 h-3 rounded-full bg-[#1AA7DE] inline-block"></span>
                        <span className="font-semibold text-slate-700">
                            {props.legendText || (isProduct ? 'Total Products' : 'Total Orders')}
                        </span>
                    </div>
                    {props.extraSummary && (
                        <span className="text-slate-500 font-medium">
                            {props.extraSummary}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Graph;